import { Alert, Button, Card, Input, InputNumber, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { PolicyData, RewardLevel } from '../types';
import { addLevel, num, parseNumberList, removeLastLevel, setLevelField } from '../utils';
import styles from '../style.module.scss';

interface Props {
  value: PolicyData;
  groups: string[];
  onChange: (next: PolicyData) => void;
}

type Tier = PolicyData['newCreator']['tiers'][number];
type CutoffKey = '28' | '29' | '30' | '31';

const moneyFormatter = (v?: number): string => String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const moneyParser = (v?: string): number => Number(String(v || '').replace(/\D/g, ''));
const moneyProps = { min: 0, precision: 0, style: { width: '100%' }, formatter: moneyFormatter, parser: moneyParser };
const intProps = { min: 0, precision: 0, style: { width: '100%' } };

const REFERENCE_LEVEL_OPTIONS = [
  { value: 'STRICT', label: 'Đủ KC + ngày + giờ (mặc định)' },
  { value: 'KC_ONLY', label: 'Chỉ KC — giống web cũ' },
];
const NEW_CREATOR_TIER_OPTIONS = [
  { value: 'FALLBACK', label: 'Hạ hạng khi thiếu giờ (mặc định)' },
  { value: 'KC_BAND_LEGACY', label: 'Theo dải KC — giống web cũ' },
];
const MONTH_INDEX_OPTIONS = [
  { value: 'JOIN_DATE', label: 'Theo ngày gia nhập (mặc định)' },
  { value: 'HAS_PREV_DATA', label: 'Theo dữ liệu tháng trước — giống web cũ' },
];
const LEGACY_VALUES = ['KC_ONLY', 'KC_BAND_LEGACY', 'HAS_PREV_DATA'];

/** 9 khối sửa Chính sách Thưởng Online (task-13: mức thưởng, NV B/C/D, mốc ngày, quy đổi, nhóm loại trừ, khoản tay, công tắc luật). */
const PolicyEditor = ({ value, groups, onChange }: Props) => {
  const [rankText, setRankText] = useState(() => value.dailyRank.rewards.map((n) => num(n)).join(', '));
  const [rankError, setRankError] = useState<string | null>(null);
  const rewardsKey = value.dailyRank.rewards.join(',');

  useEffect(() => {
    setRankText(value.dailyRank.rewards.map((n) => num(n)).join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardsKey]);

  const changeRankText = (text: string) => {
    setRankText(text);
    const parsed = parseNumberList(text);
    if (parsed === null) {
      setRankError('Danh sách không hợp lệ — dùng dạng "500.000, 200.000, 100.000, 100.000".');
      return;
    }
    setRankError(null);
    onChange({ ...value, dailyRank: { rewards: parsed } });
  };

  const setTier = (i: number, key: keyof Tier, v: number) =>
    onChange({
      ...value,
      newCreator: { ...value.newCreator, tiers: value.newCreator.tiers.map((t, idx) => (idx === i ? { ...t, [key]: v } : t)) },
    });

  const setCutoff = (key: CutoffKey, v: number) => {
    const next = { ...value.firstMonthCutoff };
    next[key] = v;
    onChange({ ...value, firstMonthCutoff: next });
  };

  const setPkqtKc = (i: number, v: number) =>
    onChange({ ...value, presets: { ...value.presets, pkqt: value.presets.pkqt.map((p, j) => (j === i ? ([v, p[1]] as [number, number]) : p)) } });
  const setPkqtAmount = (i: number, v: number) =>
    onChange({ ...value, presets: { ...value.presets, pkqt: value.presets.pkqt.map((p, j) => (j === i ? ([p[0], v] as [number, number]) : p)) } });
  const setBirthdayLabel = (i: number, v: string) =>
    onChange({ ...value, presets: { ...value.presets, birthday: value.presets.birthday.map((b, j) => (j === i ? ([v, b[1]] as [string, number]) : b)) } });
  const setBirthdayAmount = (i: number, v: number) =>
    onChange({ ...value, presets: { ...value.presets, birthday: value.presets.birthday.map((b, j) => (j === i ? ([b[0], v] as [string, number]) : b)) } });
  const setSpecialEventKc = (v: number) =>
    onChange({ ...value, presets: { ...value.presets, specialEvent: [v, value.presets.specialEvent[1]] as [number, number] } });
  const setSpecialEventAmount = (v: number) =>
    onChange({ ...value, presets: { ...value.presets, specialEvent: [value.presets.specialEvent[0], v] as [number, number] } });

  const legacyActive =
    LEGACY_VALUES.includes(value.rules.referenceLevel) ||
    LEGACY_VALUES.includes(value.rules.newCreatorTier) ||
    LEGACY_VALUES.includes(value.rules.monthIndex);

  const levelColumns: ColumnsType<RewardLevel> = [
    { title: 'Bậc', key: 'level', width: 90, render: (_, r) => `Bậc ${r.level}` },
    {
      title: 'Ngày', key: 'days', width: 100,
      render: (_, r, i) => <InputNumber {...intProps} value={r.days} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'days', v ?? 0) })} />,
    },
    {
      title: 'Giờ', key: 'hours', width: 100,
      render: (_, r, i) => <InputNumber {...intProps} value={r.hours} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'hours', v ?? 0) })} />,
    },
    {
      title: 'KC', key: 'kc', width: 140,
      render: (_, r, i) => <InputNumber {...moneyProps} value={r.kc} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'kc', v ?? 0) })} />,
    },
    {
      title: 'Giảm bậc (đ)', key: 'down', width: 150,
      render: (_, r, i) => <InputNumber {...moneyProps} value={r.down} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'down', v ?? 0) })} />,
    },
    {
      title: 'Giữ bậc (đ)', key: 'keep', width: 150,
      render: (_, r, i) => <InputNumber {...moneyProps} value={r.keep} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'keep', v ?? 0) })} />,
    },
    {
      title: 'Tăng bậc (đ)', key: 'up', width: 150,
      render: (_, r, i) => <InputNumber {...moneyProps} value={r.up} onChange={(v) => onChange({ ...value, levels: setLevelField(value.levels, i, 'up', v ?? 0) })} />,
    },
  ];

  const tierColumns: ColumnsType<Tier> = [
    { title: 'Hạng', key: 'tier', width: 80, render: (_, __, i) => `Hạng ${i + 1}` },
    { title: 'KC tối thiểu', key: 'kcMin', render: (_, t, i) => <InputNumber {...moneyProps} value={t.kcMin} onChange={(v) => setTier(i, 'kcMin', v ?? 0)} /> },
    { title: 'Giờ tối thiểu', key: 'hours', render: (_, t, i) => <InputNumber {...intProps} value={t.hours} onChange={(v) => setTier(i, 'hours', v ?? 0)} /> },
    { title: 'Tháng 1 (đ)', key: 'month1', render: (_, t, i) => <InputNumber {...moneyProps} value={t.month1} onChange={(v) => setTier(i, 'month1', v ?? 0)} /> },
    { title: 'Tháng 2 (đ)', key: 'month2', render: (_, t, i) => <InputNumber {...moneyProps} value={t.month2} onChange={(v) => setTier(i, 'month2', v ?? 0)} /> },
  ];

  return (
    <>
      <Card size="small" title="Bảng bậc" className={styles.section}>
        <Table<RewardLevel>
          rowKey="level"
          size="small"
          pagination={false}
          dataSource={value.levels}
          columns={levelColumns}
          scroll={{ x: 900, y: 420 }}
        />
        <Space style={{ marginTop: 12 }}>
          <Button onClick={() => onChange({ ...value, levels: addLevel(value.levels) })}>Thêm bậc</Button>
          <Button onClick={() => onChange({ ...value, levels: removeLastLevel(value.levels) })}>Bớt bậc cuối</Button>
        </Space>
      </Card>

      <Card size="small" title="NV B · Creator mới" className={styles.section}>
        <Space style={{ marginBottom: 12 }}>
          <span>Số ngày tối thiểu</span>
          <InputNumber {...intProps} style={{ width: 120 }} value={value.newCreator.minDays} onChange={(v) => onChange({ ...value, newCreator: { ...value.newCreator, minDays: v ?? 0 } })} />
        </Space>
        <Table<Tier> rowKey={(_, i) => String(i)} size="small" pagination={false} dataSource={value.newCreator.tiers} columns={tierColumns} />
      </Card>

      <Card size="small" title="NV C · Chuyên cần" className={styles.section}>
        <Space wrap size="large">
          <span>
            Áp dụng từ tháng thứ{' '}
            <InputNumber {...intProps} min={1} style={{ width: 90 }} value={value.attendance.fromMonthIndex} onChange={(v) => onChange({ ...value, attendance: { ...value.attendance, fromMonthIndex: v ?? 1 } })} />
          </span>
          <span>
            Số ngày <InputNumber {...intProps} style={{ width: 90 }} value={value.attendance.days} onChange={(v) => onChange({ ...value, attendance: { ...value.attendance, days: v ?? 0 } })} />
          </span>
          <span>
            Số giờ <InputNumber {...intProps} style={{ width: 90 }} value={value.attendance.hours} onChange={(v) => onChange({ ...value, attendance: { ...value.attendance, hours: v ?? 0 } })} />
          </span>
          <span>
            KC tăng thêm <InputNumber {...moneyProps} style={{ width: 140 }} value={value.attendance.kcGrowth} onChange={(v) => onChange({ ...value, attendance: { ...value.attendance, kcGrowth: v ?? 0 } })} />
          </span>
          <span>
            Thưởng (đ) <InputNumber {...moneyProps} style={{ width: 140 }} value={value.attendance.reward} onChange={(v) => onChange({ ...value, attendance: { ...value.attendance, reward: v ?? 0 } })} />
          </span>
        </Space>
      </Card>

      <Card size="small" title="NV D · Thưởng hạng ngày" className={styles.section}>
        <Input
          value={rankText}
          placeholder="500.000, 200.000, 100.000, 100.000"
          status={rankError ? 'error' : undefined}
          onChange={(e) => changeRankText(e.target.value)}
        />
        {rankError && (
          <Typography.Text type="danger" style={{ display: 'block', marginTop: 6 }}>
            {rankError}
          </Typography.Text>
        )}
      </Card>

      <Card size="small" title="Mốc ngày gia nhập & tháng mốc" className={styles.section}>
        <Space wrap size="large">
          {(['28', '29', '30', '31'] as CutoffKey[]).map((d) => (
            <span key={d}>
              Tháng {d} ngày: mốc ngày{' '}
              <InputNumber {...intProps} min={1} max={Number(d)} style={{ width: 90 }} value={value.firstMonthCutoff[d]} onChange={(v) => setCutoff(d, v ?? 1)} />
            </span>
          ))}
          <span>
            Số tháng lùi tìm tháng mốc{' '}
            <InputNumber {...intProps} min={1} max={12} style={{ width: 90 }} value={value.lookbackMonths} onChange={(v) => onChange({ ...value, lookbackMonths: v ?? 1 })} />
          </span>
        </Space>
      </Card>

      <Card size="small" title="Thu nhập tự rút" className={styles.section}>
        <Space size="large">
          <span>
            KC / USD <InputNumber {...intProps} min={1} style={{ width: 120 }} value={value.income.kcPerUsd} onChange={(v) => onChange({ ...value, income: { ...value.income, kcPerUsd: v ?? 1 } })} />
          </span>
          <span>
            VND / USD <InputNumber {...moneyProps} min={1} style={{ width: 140 }} value={value.income.vndPerUsd} onChange={(v) => onChange({ ...value, income: { ...value.income, vndPerUsd: v ?? 1 } })} />
          </span>
        </Space>
      </Card>

      <Card size="small" title="Nhóm loại trừ" className={styles.section}>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          value={value.excludedGroups}
          options={groups.map((g) => ({ value: g, label: g }))}
          onChange={(v) => onChange({ ...value, excludedGroups: v })}
        />
      </Card>

      <Card size="small" title="Mốc khoản tay" className={styles.section}>
        <Typography.Text strong>PKQT</Typography.Text>
        <Space direction="vertical" style={{ width: '100%', margin: '8px 0 16px' }}>
          {value.presets.pkqt.map((p, i) => (
            <Space key={i}>
              <span>KC</span>
              <InputNumber {...moneyProps} style={{ width: 140 }} value={p[0]} onChange={(v) => setPkqtKc(i, v ?? 0)} />
              <span>→ Tiền</span>
              <InputNumber {...moneyProps} style={{ width: 140 }} value={p[1]} onChange={(v) => setPkqtAmount(i, v ?? 0)} />
            </Space>
          ))}
        </Space>
        <Typography.Text strong>Sinh nhật</Typography.Text>
        <Space direction="vertical" style={{ width: '100%', margin: '8px 0 16px' }}>
          {value.presets.birthday.map((b, i) => (
            <Space key={i}>
              <Input style={{ width: 220 }} value={b[0]} onChange={(e) => setBirthdayLabel(i, e.target.value)} />
              <span>→ Tiền</span>
              <InputNumber {...moneyProps} style={{ width: 140 }} value={b[1]} onChange={(v) => setBirthdayAmount(i, v ?? 0)} />
            </Space>
          ))}
        </Space>
        <Typography.Text strong>Sự kiện đặc biệt</Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space>
            <span>KC</span>
            <InputNumber {...moneyProps} style={{ width: 140 }} value={value.presets.specialEvent[0]} onChange={(v) => setSpecialEventKc(v ?? 0)} />
            <span>→ Tiền</span>
            <InputNumber {...moneyProps} style={{ width: 140 }} value={value.presets.specialEvent[1]} onChange={(v) => setSpecialEventAmount(v ?? 0)} />
          </Space>
        </div>
      </Card>

      <Card size="small" title="Công tắc luật">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <span>
            Mốc đối chiếu bậc:{' '}
            <Select
              style={{ width: 360 }}
              value={value.rules.referenceLevel}
              options={REFERENCE_LEVEL_OPTIONS}
              onChange={(v) => onChange({ ...value, rules: { ...value.rules, referenceLevel: v as PolicyData['rules']['referenceLevel'] } })}
            />
          </span>
          <span>
            Hạng Creator mới:{' '}
            <Select
              style={{ width: 360 }}
              value={value.rules.newCreatorTier}
              options={NEW_CREATOR_TIER_OPTIONS}
              onChange={(v) => onChange({ ...value, rules: { ...value.rules, newCreatorTier: v as PolicyData['rules']['newCreatorTier'] } })}
            />
          </span>
          <span>
            Tháng thứ mấy:{' '}
            <Select
              style={{ width: 360 }}
              value={value.rules.monthIndex}
              options={MONTH_INDEX_OPTIONS}
              onChange={(v) => onChange({ ...value, rules: { ...value.rules, monthIndex: v as PolicyData['rules']['monthIndex'] } })}
            />
          </span>
        </Space>
        {legacyActive && (
          <Alert style={{ marginTop: 12 }} type="warning" showIcon message="Chỉ dùng để đối chiếu số đã trả theo web cũ." />
        )}
      </Card>
    </>
  );
};

export default PolicyEditor;
